// #region imports

// --- Angular
import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

// --- RxJs
import { Observable, from, BehaviorSubject, Subject, Subscription } from 'rxjs';
import { filter, take, tap, map } from 'rxjs/operators';

// --- 3rd Party
import {
  UserManager,
  OidcClient,
  Log,
  User as OidcUser,
  SigninRequest,
  SignoutRequest,
  UserManagerSettings
} from 'oidc-client';

// --- HS Code
import { environment } from '../../../../environments/environment';
import { Constants, OidcEvents, StorageKeys } from '../../../models';
import { EnvService } from '../env.service';
import { CheckSessionService } from './check-session.service';
import { Claims } from './claims';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class OidcService {
  private _isLoading$: BehaviorSubject<boolean>;
  private _identity$: BehaviorSubject<OidcUser | null>;

  private _isParticipant$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  get isParticipant$(): Observable<boolean> {
    return this._isParticipant$.asObservable();
  }

  private _isRespondent$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  get isRespondent$(): Observable<boolean> {
    return this._isRespondent$.asObservable();
  }

  private _isMobileUser$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  get isMobileUser$(): Observable<boolean> {
    return this._isMobileUser$.asObservable();
  }

  private _claims$: BehaviorSubject<Claims> = new BehaviorSubject<Claims>(new Claims());
  get claims$(): Observable<Claims> {
    return this._claims$.asObservable();
  }

  private _isImpersonateUser$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  get isImpersonateUser$(): Observable<boolean> {
    return this._isImpersonateUser$.asObservable();
  }


  private _oidcUserManager: UserManager;
  private _oidcClient: OidcClient;
  private _user: OidcUser | null = null;

  private _useCallbackFlag = true;

  private _oidcSettings: UserManagerSettings;
  private _startCheckSession: boolean;
  private _sessionChangedSubscription: Subscription;
  private _identitySubscription: Subscription;

  private _loginChanged$: Subject<boolean> = new Subject<boolean>();
  public loginChanged$(): Observable<boolean> {
    return this._loginChanged$.asObservable();
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private checkSessionService: CheckSessionService,
    private envService: EnvService
  ) {
    this._isLoading$ = new BehaviorSubject<boolean>(false);

    const logSettings = environment.authSettings.authLogger;
    const clientSettings = environment.authSettings;
    const clientId = this.envService.clientId;
    const redirectUri = this.envService.redirectUri;
    const postLogoutRedirectUri = this.envService.postLogoutRedirectUri;
    const silentRenewUri = this.envService.silentRenewUri;

    if (environment.useCallbackFlag != null) {
      this._useCallbackFlag = environment.useCallbackFlag;
    } else {
      this._useCallbackFlag = true;
    }

    if (logSettings) {
      Log.level = logSettings.level;
      Log.logger = logSettings.logger;
    }

    this._oidcSettings = {
      authority: clientSettings.authority,
      response_type: clientSettings.response_type,
      scope: clientSettings.scope,
      automaticSilentRenew: clientSettings.automaticSilentRenew,
      client_id: clientId,
      redirect_uri: redirectUri,
      post_logout_redirect_uri: postLogoutRedirectUri,
      silent_redirect_uri: silentRenewUri,
    };

    this._oidcUserManager = new UserManager(this._oidcSettings);
    this.registerOidcEvent(OidcEvents.AccessTokenExpired, _ => {
      this._loginChanged$.next(false);
    });
    this._oidcClient = new OidcClient(this._oidcSettings);

    this._isLoading$ = new BehaviorSubject<boolean>(false);
    this._identity$ = new BehaviorSubject<OidcUser | null>(null);

    this._identitySubscription = this.identity$.subscribe((oidcUser: OidcUser | null) => {
      if (!oidcUser || !oidcUser.profile) return;

      this._loginChanged$.next(true);

      const hasParticipantClaim: boolean = ((oidcUser.profile[Constants.CLAIMS_SURVEY_PARTICIPANT] ?? 'false') as string).toLowerCase() === 'true';
      const hasRespondentClaim: boolean = ((oidcUser.profile[Constants.CLAIMS_SURVEY_RESPONDENT] ?? 'false') as string).toLowerCase() === 'true';
      const hasMobileUserClaim: boolean = ((oidcUser.profile[Constants.CLAIMS_MOBILE_USER] ?? 'false') as string).toLowerCase() === 'true';
      const hasImpersonateUserClaim: boolean = ((oidcUser.profile[Constants.CLAIMS_IMPERSONATION_SESSION] ?? 'false') as string).toLowerCase() === 'true';

      const claims = new Claims();
      claims.loadClaims(oidcUser.profile);

      this._isParticipant$.next(hasParticipantClaim);
      this._isRespondent$.next(hasRespondentClaim);
      this._isMobileUser$.next(hasMobileUserClaim);
      this._isImpersonateUser$.next(hasImpersonateUserClaim);
      this._claims$.next(claims);
    });

    this._sessionChangedSubscription =
      this.checkSessionService.checkSessionChanged$.subscribe(hasChanged => {
      if (!!hasChanged) {
        this.signOutRedirect();
      }
    });
    this._startCheckSession = clientSettings.startCheckSession;
  }

  get identity$(): Observable<OidcUser | null> {
    return from(this._oidcUserManager.getUser());
  }

  getUserManager(): UserManager {
    return this._oidcUserManager;
  }

  getOidcClient(): OidcClient {
    return this._oidcClient;
  }

  waitForAuthenticationLoaded(): Observable<boolean> {
    return this._isLoading$.pipe(
      filter(loading => loading === false),
      take(1)
    );
  }

  getOidcUser(): void {
    this._isLoading$.next(true);
    from(this._oidcUserManager.getUser()).subscribe((oidcUser: OidcUser | null) => {
      this._isLoading$.next(false);
      const automaticSilentRenew =
        this._oidcSettings.automaticSilentRenew != null &&
        this._oidcSettings.automaticSilentRenew;
      if (
        oidcUser != null &&
        oidcUser.expired === true &&
        automaticSilentRenew === true
      ) {
        this.signInSilent();
      } else {
        this._identity$.next(oidcUser);
      }
    });
  }

  removeOidcUser(): Observable<void> {
    return from(this._oidcUserManager.removeUser());
  }

  registerOidcEvent(event: OidcEvents, callback: (...ev: any[]) => void) {
    switch (event) {
      case OidcEvents.AccessTokenExpired:
        this._oidcUserManager.events.addAccessTokenExpired(callback);
        break;
      case OidcEvents.AccessTokenExpiring:
        this._oidcUserManager.events.addAccessTokenExpiring(callback);
        break;
      case OidcEvents.SilentRenewError:
        this._oidcUserManager.events.addSilentRenewError(callback);
        break;
      case OidcEvents.UserLoaded:
        this._oidcUserManager.events.addUserLoaded(callback);
        break;
      case OidcEvents.UserSessionChanged:
        this._oidcUserManager.events.addUserSessionChanged(callback);
        break;
      case OidcEvents.UserSignedOut:
        this._oidcUserManager.events.addUserSignedOut(callback);
        break;
      case OidcEvents.UserUnloaded:
        this._oidcUserManager.events.addUserUnloaded(callback);
        break;
      default:
        break;
    }
  }

  removeOidcEvent(event: OidcEvents, callback: (...ev: any[]) => void) {
    switch (event) {
      case OidcEvents.AccessTokenExpired:
        this._oidcUserManager.events.removeAccessTokenExpired(callback);
        break;
      case OidcEvents.AccessTokenExpiring:
        this._oidcUserManager.events.removeAccessTokenExpiring(callback);
        break;
      case OidcEvents.SilentRenewError:
        this._oidcUserManager.events.removeSilentRenewError(callback);
        break;
      case OidcEvents.UserLoaded:
        this._oidcUserManager.events.removeUserLoaded(callback);
        break;
      case OidcEvents.UserSessionChanged:
        this._oidcUserManager.events.removeUserSessionChanged(callback);
        break;
      case OidcEvents.UserSignedOut:
        this._oidcUserManager.events.removeUserSignedOut(callback);
        break;
      case OidcEvents.UserUnloaded:
        this._oidcUserManager.events.removeUserUnloaded(callback);
        break;
      default:
        break;
    }
  }

  signInPopup(args?: any): Observable<OidcUser> {
    this.setCallbackInformation(true);

    return from(this._oidcUserManager.signinPopup({ ...args }));
  }

  signInRedirect(args?: any): Observable<void> {
    this.setCallbackInformation(false);

    return from(this._oidcUserManager.signinRedirect({ ...args }));
  }
  public finishLogin = (): Promise<OidcUser> => {
    return this._oidcUserManager.signinRedirectCallback()
    .then(user => {
      this._loginChanged$.next(this._checkUser(user));
      return user;
    })
  }

  signOutPopup(args?: any): Observable<any> {
    this.setCallbackInformation(true);

    return from(this._oidcUserManager.signoutPopup({ ...args }));
  }

  signOutRedirect(args?: any): Observable<any> {
    this.setCallbackInformation(false);

    return from(this._oidcUserManager.signoutRedirect({ ...args }));
  }

  signout(args?: any): Promise<void> {
    const result = this._oidcUserManager.signoutRedirect({...args});
    return result;
  }

  public finishLogout = () => {
    this._user = null;
    this._loginChanged$.next(false);

    const storedIsPopupCallbackFlag = localStorage.getItem('ngoidc:isPopupCallback');
    const isPopupCallback = storedIsPopupCallbackFlag && JSON.parse(storedIsPopupCallbackFlag);

    if (isPopupCallback) {
      return this._oidcUserManager.signoutPopupCallback();
    } else {
      return this._oidcUserManager.signoutRedirectCallback();
    }
  }

  signInSilent(args?: any): void {
    this.signInSilent$(args).subscribe(
      (oidcUser: OidcUser) => {
        this._identity$.next(oidcUser);
        if (this._startCheckSession && this.checkSessionService.isCheckSessionConfigured()) {
          this.checkSessionService.start(oidcUser);
        }
      }
    );
  }

  signInSilent$(args?: any): Observable<OidcUser> {
    return from(this._oidcUserManager.signinSilent({ ...args }));
  }

  signinPopupCallback(): Observable<any> {
    return from(this._oidcUserManager.signinPopupCallback()).pipe(
      tap((oidcUser: OidcUser | undefined) => {
        if (oidcUser && this._startCheckSession && this.checkSessionService.isCheckSessionConfigured()) {
          this.checkSessionService.start(oidcUser);
        }
      })
    );
  }

  signinRedirectCallback(): Observable<OidcUser> {
    return from(this._oidcUserManager.signinRedirectCallback()).pipe(
      tap((oidcUser: OidcUser) => {
        if (this._startCheckSession && this.checkSessionService.isCheckSessionConfigured()) {
          this.checkSessionService.start(oidcUser);
        }
      })
    );
  }

  signoutPopupCallback(): Observable<void> {
    return from(this._oidcUserManager.signoutPopupCallback()).pipe(
      tap(() => {
        if (this._startCheckSession && this.checkSessionService.isCheckSessionConfigured()) {
          this.checkSessionService.stop();
        }
      })
    );
  }

  signoutRedirectCallback(): Observable<any> {
    return from(this._oidcUserManager.signoutRedirectCallback()).pipe(
      tap(() => {
        if (this._startCheckSession && this.checkSessionService.isCheckSessionConfigured()) {
          this.checkSessionService.stop();
        }
      })
    );
  }

  getSigninUrl(args?: any): Observable<SigninRequest> {
    return from(this._oidcUserManager.createSigninRequest(args));
  }

  getSignoutUrl(args?: any): Observable<SignoutRequest> {
    return from(this._oidcUserManager.createSignoutRequest(args));
  }

  public isAuthenticated = (): Promise<boolean> => {
    return this._oidcUserManager.getUser()
    .then(user => {
      if(this._user !== user){
        this._loginChanged$.next(this._checkUser(user));
      }

      this._user = user;

      return this._checkUser(user);
    })
  }

  private setCallbackInformation(isPopupCallback: boolean) {
    // is browser and useCallbackFlag set to true or defaults to true
    if (isPlatformBrowser(this.platformId) && this._useCallbackFlag) {
      localStorage.setItem(StorageKeys.PopupCallback, `${isPopupCallback}`);
    }
  }

  isCurrentlyImpersonate():boolean
  {
    return this._isImpersonateUser$.value;
  }


  private _checkUser = (user : OidcUser | null): boolean => {
    return !!user && !user.expired;
  }
}
